import {
  AccountTrackerConfig,
  BaseConfig,
  BaseCurrencyControllerConfig,
  BaseEmbedControllerState,
  BaseState,
  KeyringControllerState,
  PreferencesConfig,
  PreferencesState,
  TransactionConfig,
  TransactionState,
  // SafeEventEmitterProvider,
} from "@toruslabs/base-controllers";
import { LOGIN_PROVIDER, OpenloginUserInfo } from "@toruslabs/openlogin-utils";
import {
  ChainNetworksConfig,
  ChainNetworksState,
  ExtendedAddressPreferences,
  LedgerState,
  MessageControllerState,
  NftTrackerConfig,
  NftTrackerState,
  TokensInfoConfig,
  TokensInfoState,
  TokensTrackerConfig,
  TokensTrackerState,
  TransactionJSONType,
  XrplAccountTrackerState,
  XrplCurrencyControllerState,
  XrplNetworkConfig,
  XrplNetworkState,
  XrplProviderConfig,
} from "@toruslabs/xrpl-controllers";

export const LOCAL_STORAGE_KEY = "localStorage";
export const SESSION_STORAGE_KEY = "sessionStorage";
export type STORAGE_TYPE = typeof LOCAL_STORAGE_KEY | typeof SESSION_STORAGE_KEY;

export type AccountKey = {
  privKey: string;
  accountType: string;
};
export type OpenLoginPopupResponse = {
  userInfo: OpenloginUserInfo;
  keys: AccountKey[];
  sessionId: string;
  sessionNamespace: string;
};

export interface TorusControllerState extends BaseState {
  NetworkControllerState: XrplNetworkState;
  CurrencyControllerState: XrplCurrencyControllerState;
  PreferencesControllerState: Partial<PreferencesState<ExtendedAddressPreferences>>;
  AccountTrackerState: XrplAccountTrackerState;
  KeyringControllerState: KeyringControllerState;
  MessageControllerState: MessageControllerState;
  TransactionControllerState: TransactionState<TransactionJSONType>;
  EmbedControllerState: BaseEmbedControllerState;
  TokensInfoState: TokensInfoState;
  TokensTrackerState: TokensTrackerState;
  NftTrackerState: NftTrackerState;
  ChainConfigNetworksState: ChainNetworksState;
}

export interface TorusControllerConfig extends BaseConfig {
  NetworkControllerConfig: XrplNetworkConfig;
  ChainConfigControllerConfig: ChainNetworksConfig;
  CurrencyControllerConfig: BaseCurrencyControllerConfig;
  PreferencesControllerConfig: Partial<PreferencesConfig> & Pick<PreferencesConfig, "api" | "signInPrefix" | "commonApiHost">;
  AccountTrackerConfig: AccountTrackerConfig<LedgerState>;
  KeyringControllerConfig: BaseConfig;
  TransactionControllerConfig: TransactionConfig;
  TokensInfoConfig: TokensInfoConfig;
  TokensTrackerConfig: TokensTrackerConfig;
  NftTrackerConfig: NftTrackerConfig;
}

export const CONTROLLER_MODULE_KEY = "controllerModule";

export interface ControllerModuleState {
  torusState: TorusControllerState;
}

export const NAVIGATION_LIST = {
  home: {
    name: "navBar.home",
    route: "home",
    icon: "home-solid-icon",
    desktop: true,
  },
  activity: {
    name: "navBar.activity",
    route: "activity",
    icon: "switch-vertical-icon",
    desktop: true,
  },
  profile: {
    name: "navBar.profile",
    route: "profile",
    icon: "user-circle-icon",
    desktop: true,
  },
  settings: {
    name: "navBar.settings",
    route: "settings",
    icon: "adjustments-icon",
    desktop: true,
  },
};

export const DEFAULT_USER_INFO = {
  aggregateVerifier: "",
  email: "",
  name: "",
  profileImage: "",
  typeOfLogin: LOGIN_PROVIDER.GOOGLE,
  verifier: "",
  verifierId: "",
};

export const XRPL = "xrpl";
// export const UNSTOPPABLE_DOMAINS = "Unstoppable_Domains";
export const GOOGLE = "google";
export const FACEBOOK = "facebook";
export const REDDIT = "reddit";
export const DISCORD = "discord";
export const TWITCH = "twitch";
export const APPLE = "apple";
export const LINE = "line";
export const GITHUB = "github";
export const KAKAO = "kakao";
export const LINKEDIN = "linkedin";
export const TWITTER = "twitter";
export const WEIBO = "weibo";
export const WECHAT = "wechat";
export const EMAIL_PASSWORD = "email_password";
export const PASSWORDLESS = "passwordless";
export const JWT = "jwt";
export interface TransferType {
  name: string;
  value: string;
}

export const ALLOWED_VERIFIERS: TransferType[] = [
  {
    name: "walletSettings.xrplAddress",
    value: XRPL,
  },
];

export const ALLOWED_VERIFIERS_ERRORS: Record<string, string> = {
  [XRPL]: "walletSettings.invalidXrpl",
};

export type TX_TYPE = "Transfer" | "Deploy" | "Buy Crypto";

export const STATUS = {
  DEFAULT: "default",
  SUCCESS: "success",
  DANGER: "danger",
  INFO: "info",
} as const;

export type STATUS_TYPE = (typeof STATUS)[keyof typeof STATUS];

export const LOCALE_EN = "en";
export const LOCALE_DE = "de";
export const LOCALE_JA = "ja";
export const LOCALE_KO = "ko";
export const LOCALE_ZH = "zh";
export const LOCALE_ES = "es";
export const LOCALE_EN_LABEL = "English";
export const LOCALE_DE_LABEL = "German (Deutsch)";
export const LOCALE_JA_LABEL = "Japanese (日本語)";
export const LOCALE_KO_LABEL = "Korean (한국어)";
export const LOCALE_ZH_LABEL = "Mandarin (中文)";
export const LOCALE_ES_LABEL = "Spanish (Español)";

export const LOCALES = [
  {
    name: LOCALE_EN_LABEL,
    value: LOCALE_EN,
  },
  {
    name: LOCALE_DE_LABEL,
    value: LOCALE_DE,
  },
  {
    name: LOCALE_JA_LABEL,
    value: LOCALE_JA,
  },
  {
    name: LOCALE_KO_LABEL,
    value: LOCALE_KO,
  },
  {
    name: LOCALE_ZH_LABEL,
    value: LOCALE_ZH,
  },
  {
    name: LOCALE_ES_LABEL,
    value: LOCALE_ES,
  },
];

export const BUTTON_POSITION = {
  BOTTOM_LEFT: "bottom-left",
  TOP_LEFT: "top-left",
  BOTTOM_RIGHT: "bottom-right",
  TOP_RIGHT: "top-right",
} as const;

export type BUTTON_POSITION_TYPE = (typeof BUTTON_POSITION)[keyof typeof BUTTON_POSITION];
export interface EmbedInitParams {
  buttonPosition: BUTTON_POSITION_TYPE;
  isIFrameFullScreen: boolean;
  apiKey: string;
  network: XrplProviderConfig;
  dappMetadata: {
    name: string;
    icon: string;
  };
}

export interface LOGIN_CONFIG {
  loginProvider: string;
  name: string;
  description: string;
  logoHover: string;
  logoLight: string;
  logoDark: string;
  showOnModal: boolean;
  mainOption: boolean;
  showOnDesktop: boolean;
  showOnMobile: boolean;
  hasLightLogo: boolean;
  buttonDescription: string;
}

export const BLOCKCHAIN_OPTIONS = {
  XRP_LEDGER: "XRP Ledger",
  SOLANA: "Solana",
  ETHEREUM: "Ethereum",
  POLYGON: "Polygon",
  BINANCE: "Binance",
};
